<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppointmentStore } from '@/stores/appointmentStore';
import WeekCalendar from './WeekCalendar.vue';

const { t } = useI18n();
const appointmentStore = useAppointmentStore();

const intlLocale = computed(() => 'fr-FR');

// Étapes : 1 = choix créneau, 2 = formulaire, 3 = succès
const step = ref<1 | 2 | 3>(1);

// Bloquer le scroll du body et réinitialiser l'étape quand le modal s'ouvre/ferme
watch(() => appointmentStore.isModalOpen, (open) => {
  if (open) {
    document.body.style.overflow = 'hidden';
    step.value = 1;
    resetForm();
  } else {
    document.body.style.overflow = '';
  }
});

// S'assurer de restaurer le scroll si le composant est démonté avec le modal ouvert
onUnmounted(() => {
  document.body.style.overflow = '';
});

// Formulaire
const form = ref({
  name: '',
  email: '',
  phone: '',
  subject: ''
});
const formErrors = ref<Record<string, string>>({});
const submitError = ref('');

watch(() => appointmentStore.bookingSuccess, (success) => {
  if (success) step.value = 3;
});

function resetForm() {
  form.value = { name: '', email: '', phone: '', subject: '' };
  formErrors.value = {};
  submitError.value = '';
}

function goToStep2() {
  if (!appointmentStore.selectedSlot) return;
  step.value = 2;
  submitError.value = '';
}

// Appelé directement depuis WeekCalendar au clic sur un créneau
function onSlotSelected() {
  goToStep2();
}

function goBack() {
  step.value = 1;
  submitError.value = '';
}

function validateForm(): boolean {
  const errors: Record<string, string> = {};

  if (!form.value.name.trim()) {
    errors.name = t('booking.errNameRequired');
  } else if (form.value.name.trim().length < 2) {
    errors.name = t('booking.errNameMin');
  }

  if (!form.value.email.trim()) {
    errors.email = t('booking.errEmailRequired');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
    errors.email = t('booking.errEmailInvalid');
  }

  if (!form.value.phone.trim()) {
    errors.phone = t('booking.errPhoneRequired');
  } else if (!/^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/.test(form.value.phone.trim())) {
    errors.phone = t('booking.errPhoneInvalid');
  }

  formErrors.value = errors;
  return Object.keys(errors).length === 0;
}

async function submitBooking() {
  if (!validateForm()) return;
  if (!appointmentStore.selectedSlot) return;

  submitError.value = '';

  try {
    await appointmentStore.submitBooking({
      scheduled_at: appointmentStore.selectedSlot,
      name: form.value.name.trim(),
      email: form.value.email.trim(),
      phone: form.value.phone.trim(),
      subject: form.value.subject.trim() || undefined
    });
  } catch (err: any) {
    submitError.value = err.message || t('booking.submitErrorGeneric');
  }
}

function formatSlotFull(datetime: string): string {
  const d = new Date(datetime);
  return d.toLocaleDateString(intlLocale.value, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris'
  });
}

const selectedSlotFormatted = computed(() => {
  return appointmentStore.selectedSlot ? formatSlotFull(appointmentStore.selectedSlot) : '';
});

function handleOverlayClick(e: MouseEvent) {
  if ((e.target as HTMLElement).classList.contains('modal-overlay')) {
    appointmentStore.closeModal();
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="appointmentStore.isModalOpen"
        class="modal-overlay"
        @click="handleOverlayClick"
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-modal-title"
      >
        <div class="modal-container">
          <!-- Header -->
          <div class="modal-header">
            <div class="modal-header-content">
              <i class="fas fa-calendar-check modal-icon"></i>
              <div>
                <h2 id="booking-modal-title" class="modal-title">{{ t('booking.title') }}</h2>
                <p class="modal-subtitle">{{ t('booking.subtitle') }}</p>
              </div>
            </div>
            <button class="modal-close" @click="appointmentStore.closeModal()" :aria-label="t('booking.closeAria')">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <!-- Indicateur d'étapes -->
          <div class="step-indicator" v-if="step < 3">
            <div class="step" :class="{ active: step >= 1, done: step > 1 }">
              <span class="step-num">
                <i v-if="step > 1" class="fas fa-check"></i>
                <span v-else>1</span>
              </span>
              <span class="step-label">{{ t('booking.step1Label') }}</span>
            </div>
            <div class="step-line"></div>
            <div class="step" :class="{ active: step >= 2 }">
              <span class="step-num">2</span>
              <span class="step-label">{{ t('booking.step2Label') }}</span>
            </div>
          </div>

          <!-- Contenu -->
          <div class="modal-body">

            <!-- Étape 1 : Calendrier -->
            <div v-if="step === 1" class="step-content">
              <p class="step-description">
                {{ t('booking.step1Description') }}
              </p>
              <WeekCalendar @slot-selected="onSlotSelected" />
            </div>

            <!-- Étape 2 : Formulaire -->
            <div v-else-if="step === 2" class="step-content">
              <div class="selected-slot-recap">
                <i class="fas fa-calendar-alt"></i>
                <div>
                  <span class="recap-label">{{ t('booking.selectedSlotLabel') }}</span>
                  <span class="recap-value">{{ selectedSlotFormatted }}</span>
                </div>
                <button type="button" class="btn-change" @click="goBack">{{ t('booking.changeSlot') }}</button>
              </div>

              <form @submit.prevent="submitBooking" novalidate>
                <div class="form-row">
                  <div class="form-group">
                    <label for="booking-name">{{ t('booking.fullName') }} <span class="required">*</span></label>
                    <input
                      id="booking-name"
                      v-model="form.name"
                      type="text"
                      :placeholder="t('booking.placeholderName')"
                      :class="{ error: formErrors.name }"
                      autocomplete="name"
                    />
                    <span v-if="formErrors.name" class="field-error">{{ formErrors.name }}</span>
                  </div>
                  <div class="form-group">
                    <label for="booking-email">{{ t('booking.email') }} <span class="required">*</span></label>
                    <input
                      id="booking-email"
                      v-model="form.email"
                      type="email"
                      :placeholder="t('booking.placeholderEmail')"
                      :class="{ error: formErrors.email }"
                      autocomplete="email"
                    />
                    <span v-if="formErrors.email" class="field-error">{{ formErrors.email }}</span>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-group">
                    <label for="booking-phone">{{ t('booking.phone') }} <span class="required">*</span></label>
                    <input
                      id="booking-phone"
                      v-model="form.phone"
                      type="tel"
                      :placeholder="t('booking.placeholderPhone')"
                      :class="{ error: formErrors.phone }"
                      autocomplete="tel"
                    />
                    <span v-if="formErrors.phone" class="field-error">{{ formErrors.phone }}</span>
                  </div>
                  <div class="form-group">
                    <label for="booking-subject">{{ t('booking.subject') }}</label>
                    <input
                      id="booking-subject"
                      v-model="form.subject"
                      type="text"
                      :placeholder="t('booking.placeholderSubject')"
                    />
                  </div>
                </div>

                <div v-if="submitError" class="submit-error">
                  <i class="fas fa-exclamation-circle"></i>
                  {{ submitError }}
                </div>

                <div class="step-actions">
                  <button type="button" class="btn btn-outline" @click="goBack">
                    <i class="fas fa-arrow-left"></i>
                    {{ t('booking.back') }}
                  </button>
                  <button
                    type="submit"
                    class="btn btn-primary"
                    :disabled="appointmentStore.isSubmitting"
                  >
                    <i v-if="appointmentStore.isSubmitting" class="fas fa-spinner fa-spin"></i>
                    <span v-else>{{ t('booking.confirmBooking') }}</span>
                  </button>
                </div>
              </form>
            </div>

            <!-- Étape 3 : Succès -->
            <div v-else-if="step === 3" class="step-content success-step">
              <div class="success-icon">
                <i class="fas fa-check-circle"></i>
              </div>
              <h3 class="success-title">{{ t('booking.successTitle') }}</h3>
              <p class="success-text">
                {{ t('booking.successText') }}
              </p>
              <p class="success-text">
                {{ t('booking.successEmailSent') }}
              </p>
              <div class="success-details">
                <i class="fas fa-calendar-alt"></i>
                <strong>{{ selectedSlotFormatted }}</strong>
              </div>
              <p class="success-note">
                {{ t('booking.successNote') }}
              </p>
              <button type="button" class="btn btn-primary" @click="appointmentStore.closeModal()">
                {{ t('booking.close') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  backdrop-filter: blur(2px);
}

.modal-container {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.modal-header-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-icon {
  font-size: 1.5rem;
  color: var(--primary, #dc2626);
  flex-shrink: 0;
}

.modal-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-primary, #1a1a1a);
  margin: 0;
}

.modal-subtitle {
  font-size: 0.8rem;
  color: var(--text-secondary, #888);
  margin: 2px 0 0;
}

.modal-close {
  width: 36px;
  height: 36px;
  border: none;
  background: #f5f5f5;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888;
  transition: all 0.2s;
  flex-shrink: 0;
}

.modal-close:hover {
  background: #fee2e2;
  color: var(--primary, #dc2626);
}

/* Indicateur d'étapes */
.step-indicator {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  gap: 0;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.step {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.step-num {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  background: #e5e7eb;
  color: #9ca3af;
  transition: all 0.2s;
}

.step.active .step-num {
  background: var(--primary, #dc2626);
  color: white;
}

.step.done .step-num {
  background: #16a34a;
  color: white;
}

.step-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #9ca3af;
  transition: color 0.2s;
}

.step.active .step-label {
  color: var(--primary, #dc2626);
}

.step.done .step-label {
  color: #16a34a;
}

.step-line {
  flex: 1;
  height: 2px;
  background: #e5e7eb;
  margin: 0 0.75rem;
}

/* Corps */
.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.step-description {
  font-size: 0.9rem;
  color: var(--text-secondary, #666);
  margin: 0;
}

.selected-slot-preview {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  background: rgba(220, 38, 38, 0.06);
  border: 1.5px solid rgba(220, 38, 38, 0.2);
  border-radius: 8px;
  color: var(--primary, #dc2626);
  font-size: 0.9rem;
}

.selected-slot-recap {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.selected-slot-recap i {
  color: var(--primary, #dc2626);
  font-size: 1.1rem;
  flex-shrink: 0;
}

.recap-label {
  display: block;
  font-size: 0.7rem;
  color: #888;
  font-weight: 500;
}

.recap-value {
  display: block;
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--text-primary, #1a1a1a);
  text-transform: capitalize;
}

.btn-change {
  margin-left: auto;
  font-size: 0.78rem;
  color: var(--primary, #dc2626);
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 600;
  text-decoration: underline;
  flex-shrink: 0;
}

/* Formulaire */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.form-group label {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-primary, #333);
}

.required {
  color: var(--primary, #dc2626);
}

.form-group input {
  padding: 0.6rem 0.75rem;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.88rem;
  color: var(--text-primary, #1a1a1a);
  background: white;
  transition: border-color 0.2s;
  outline: none;
  width: 100%;
  box-sizing: border-box;
}

.form-group input:focus {
  border-color: var(--primary, #dc2626);
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

.form-group input.error {
  border-color: #dc2626;
}

.field-error {
  font-size: 0.75rem;
  color: #dc2626;
}

.submit-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 0.85rem;
}

/* Actions */
.step-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.btn-primary {
  background: var(--primary, #dc2626);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #b91c1c;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-outline {
  background: white;
  color: var(--text-secondary, #666);
  border: 1.5px solid #e5e7eb;
}

.btn-outline:hover {
  border-color: var(--primary, #dc2626);
  color: var(--primary, #dc2626);
}

/* Succès */
.success-step {
  align-items: center;
  text-align: center;
  padding: 1rem 0;
}

.success-icon {
  font-size: 3.5rem;
  color: #16a34a;
}

.success-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text-primary, #1a1a1a);
  margin: 0;
}

.success-text {
  font-size: 0.9rem;
  color: var(--text-secondary, #666);
  line-height: 1.6;
  margin: 0;
}

.success-details {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1.25rem;
  background: #f0fdf4;
  border: 1.5px solid #bbf7d0;
  border-radius: 8px;
  color: #16a34a;
  font-size: 0.9rem;
  text-transform: capitalize;
}

.success-note {
  font-size: 0.85rem;
  color: #888;
  margin: 0;
}

/* Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.95) translateY(10px);
  opacity: 0;
}

@media (max-width: 640px) {
  .modal-overlay {
    padding: 0;
    align-items: flex-end;
  }

  .modal-container {
    border-radius: 16px 16px 0 0;
    max-height: 95vh;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .step-actions {
    flex-direction: column-reverse;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
